import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandEnum } from "../../_common/enum/brand.enum";
import { FormTemplateTypeEnum } from "../../_common/enum/forms/formTemplateType.enum";
import { TrackingStageEnum } from "../../_common/enum/trackingStage.enum";
import { IQuoteTrackingDetail } from "../../_common/interface/quoteTrackingDetail/iQuoteTrackingDetail.interface";
import config from "../../_config/config.const";
import { LoaderDoubleLines } from "../../_components/loader/LoaderDoubleLines";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { isEmbeddedInIframe, sendBodyHeightToParent, holdCurrentIframeHeight } from "../../_helpers/iFrameSize.helper";
import {
    getOrCreateTrackingSnapshot,
    getOrCreateVisitId,
    registerFormCreationForVisit
} from "../../_helpers/trackingSnapshot.helper";
import { formService } from "../../_services/form.service";
import { quoteTracking } from "../../_services/quoteTracking.service";
import { withFormAccessToken } from "../../_helpers/formAccessToken.helper";

export function Forms(params: { brand: BrandEnum }) {
    const navigate = useNavigate();

    const resetTrackingData = (): IQuoteTrackingDetail => ({
        formId: null,
        brandId: params.brand,
        formTypeId: "",
        quoteId: "",
        trackingStageId: null,
        source: "",
        originalSource: "",
        medium: "",
        originalMedium: "",
        campaign: "",
        originalCampaign: "",
        keyword: "",
        originalKeyword: "",
        ip: "",
        trafficSource: "",
        originalReferringUrl: "",
        handlLandingPage: "",
        handlLandingPageBase: "",
        handlRef: "",
        handlOriginalRef: "",
        handlRefDomain: "",
        handlUrl: "",
        handlUrlBase: "",
        handlID: "",
        gClId: "",
        metaFbc: "",
        metaFbp: "",
        metaFbclid: "",
        isFormDuplicate: false,
        formSequenceNo: null,
        visitId: null
    });

    const [isLoading, setLoading] = useState(true);
    const [formTypeId, setFormTypeId] = useState<string | null>(null);
    const [trackingData, setTrackingData] = useState<IQuoteTrackingDetail>(resetTrackingData);

    const createdRef = useRef(false);
    const queryParamsRef = useRef<URLSearchParams>(new URLSearchParams(""));
    const pathnameRef = useRef<string>("");

    function normalizeOrigin(o: string) {
        return o ? o.replace(/\/+$/, "") : o;
    }

    function getAllowedOrigins(): string[] {
        const raw: unknown = (config as any).ORIGINS ?? (config as any).ORIGIN;
        if (Array.isArray(raw)) return raw.map(String).map(normalizeOrigin).filter(Boolean);
        if (typeof raw === "string" && raw.trim()) return [normalizeOrigin(raw)];
        return [];
    }

    const parentOriginsRef = useRef<string[]>(getAllowedOrigins());
    const isEmbeddedRef = useRef(isEmbeddedInIframe());

    const lastSerializedRef = useRef<string>("");
    const stableReadsRef = useRef<number>(0);

    useEffect(() => {
        if (isEmbeddedRef.current) holdCurrentIframeHeight();
    }, []);

    useEffect(() => {
        if (isEmbeddedRef.current) return;

        if (window.location.search) {
            const current = new URLSearchParams(window.location.search);
            queryParamsRef.current = mergeParams(queryParamsRef.current, current);
        }
        pathnameRef.current = window.location.pathname || "";
        tryResolveAndCreate();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time standalone bootstrap
    }, []);

    const COOKIE_PARAM_KEYS = [
        "utm_source", "utm_medium", "utm_campaign", "utm_term",
        "first_utm_source", "first_utm_medium", "first_utm_campaign", "first_utm_term",
        "gclid", "fbclid", "_fbc", "_fbp",
        "pysTrafficSource", "last_pysTrafficSource",
        "organic_source", "first_traffic_source",
        "handl_landing_page", "handl_original_ref", "handl_ref_domain", "traffic_source"
    ];

    function mergeParams(intoParams: URLSearchParams, incomingParams: URLSearchParams) {
        incomingParams.forEach((value, key) => {
            if (value && value !== "null" && value !== "undefined") {
                intoParams.set(key, value);
            }
        });
        return intoParams;
    }

    function getFormTypeFromPath(path: string): string | null {
        if (!path) return null;

        const p = path.toLowerCase();

        if (p.includes("life-insurance")) return FormTemplateTypeEnum.LifeInsurance.toString();
        if (p.includes("mortgage-protection")) return FormTemplateTypeEnum.MortgageProtection.toString();
        if (p.includes("executive-income-protection")) return FormTemplateTypeEnum.ExecutiveIncomeProtection.toString();
        if (p.includes("income-protection")) return FormTemplateTypeEnum.IncomeProtection.toString();
        if (p.includes("illness")) return FormTemplateTypeEnum.SeriousIllnessProtection.toString();
        if (p.includes("mortgage") && !p.includes("protection")) return FormTemplateTypeEnum.Mortgages.toString();
        if (p.includes("health-application")) return FormTemplateTypeEnum.HealthQuestionnaire.toString();

        return null;
    }

    function tryResolveAndCreate() {
        if (createdRef.current) return;

        const qp = queryParamsRef.current;
        const existingToken = qp.get("token");

        if (existingToken) {
            console.info("Existing hq token detected, skipping creation", {
                token: existingToken
            });

            createdRef.current = true;
            navigate(`/formOriginal?token=${encodeURIComponent(existingToken)}`);
            return;
        }

        let resolvedFormTypeId = qp.get("formTypeId");

        if (!resolvedFormTypeId && pathnameRef.current) {
            resolvedFormTypeId = getFormTypeFromPath(pathnameRef.current);
        }

        if (!resolvedFormTypeId) {
            console.debug("[Forms] tryResolveAndCreate: formTypeId not resolved yet", {
                pathname: pathnameRef.current,
                query: qp.toString()
            });
            return;
        }

        createdRef.current = true;

        setFormTypeId(resolvedFormTypeId);
        setTrackingData((current) => ({
            ...current,
            formTypeId: resolvedFormTypeId!
        }));

        createNewForm(
            parseInt(resolvedFormTypeId, 10),
            queryParamsRef.current
        );
    }

    useEffect(() => {
        if (formTypeId || !isEmbeddedRef.current) return;

        let attempts = 0;
        const maxAttempts = 20;
        const intervalMs = 300;
        const requiredStableReads = 3;

        const askAgain = () => {
            if (formTypeId || attempts >= maxAttempts || stableReadsRef.current >= requiredStableReads) {
                console.info("[Forms] Stopping asks. State:", {
                    attempts,
                    stableReads: stableReadsRef.current,
                    formTypeId
                });
                clearInterval(interval);
                tryResolveAndCreate();
                return;
            }

            const origins = parentOriginsRef.current;

            if (origins.length === 0) {
                window.parent.postMessage({ type: "REQUEST_QUERY_STRING" }, "*");
                window.parent.postMessage({ type: "REQUEST_PARENT_PATHNAME" }, "*");
            } else {
                for (const o of origins) {
                    try {
                        window.parent.postMessage({ type: "REQUEST_QUERY_STRING" }, o);
                        window.parent.postMessage({ type: "REQUEST_PARENT_PATHNAME" }, o);
                    } catch (err) {
                        console.warn(`[postMessage] failed for origin "${o}":`, err);
                    }
                }
            }

            attempts++;
        };

        const interval = setInterval(askAgain, intervalMs);

        const stabilize = () => {
            const serializedNow = queryParamsRef.current.toString();

            if (serializedNow === lastSerializedRef.current) {
                stableReadsRef.current++;
            } else {
                lastSerializedRef.current = serializedNow;
                stableReadsRef.current = 0;
            }

            if (attempts % 5 === 0) {
                console.debug("[Forms] stabilize tick:", {
                    attempts,
                    stableReads: stableReadsRef.current,
                    currentQuery: serializedNow
                });
            }
        };

        const stabInterval = setInterval(stabilize, intervalMs);

        return () => {
            clearInterval(interval);
            clearInterval(stabInterval);
        };
    }, [formTypeId]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const incomingOrigin = normalizeOrigin(event.origin);
            const allowed = parentOriginsRef.current;

            if (allowed.length > 0 && !allowed.includes(incomingOrigin)) {
                return;
            }

            if (createdRef.current) {
                if ((event.data || {}).type !== "REQUEST_HEIGHT") {
                    return;
                }
            }

            const parentMessageData = (event.data || {}) as {
                type?: string;
                pathname?: string;
                queryString?: string;
            };

            switch (parentMessageData.type) {
                case "QUERY_STRING": {
                    const incoming = new URLSearchParams(parentMessageData.queryString || "");
                    queryParamsRef.current = mergeParams(queryParamsRef.current, incoming);

                    const hasSignal = COOKIE_PARAM_KEYS.some((k) => queryParamsRef.current.has(k));
                    if (hasSignal) {
                        tryResolveAndCreate();
                    }
                    return;
                }

                case "PARENT_PATHNAME": {
                    if (typeof parentMessageData.pathname === "string") {
                        pathnameRef.current = parentMessageData.pathname || "";
                    }
                    tryResolveAndCreate();
                    return;
                }

                case "REQUEST_HEIGHT": {
                    sendBodyHeightToParent();
                    return;
                }

                default: {
                    if (!isEmbeddedRef.current) return;

                    const origins = allowed.length ? allowed : ["*"];
                    for (const o of origins) {
                        try {
                            window.parent.postMessage({ type: "REQUEST_PARENT_PATHNAME" }, o);
                            window.parent.postMessage({ type: "REQUEST_QUERY_STRING" }, o);
                        } catch (err) {
                            console.warn(`[postMessage] failed for origin "${o}":`, err);
                        }
                    }
                    return;
                }
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, []);

    function createNewForm(formTypeId: FormTemplateTypeEnum, queryParams: URLSearchParams) {
        setLoading(true);

        return formService
            .createByType(formTypeId, params.brand)
            .then(
                async ({ id, token }) => {
                    const snapshotParams = getOrCreateTrackingSnapshot(queryParams);
                    const { creationIndex, isDuplicate } = registerFormCreationForVisit();
                    const visitId = getOrCreateVisitId();

                    await createQuoteTracking(
                        id,
                        snapshotParams,
                        TrackingStageEnum.FormCreated,
                        isDuplicate,
                        creationIndex,
                        visitId
                    );

                    if (
                        formTypeId === FormTemplateTypeEnum.HealthQuestionnaire ||
                        formTypeId === FormTemplateTypeEnum.FactFind
                    ) {
                        navigate(withFormAccessToken(`/formOriginal/${id}`, id, token));
                    } else {
                        navigate(withFormAccessToken(`${id}`, id, token));
                    }
                },
                (err) => {
                    return handleErrorResponse(err);
                }
            )
            .finally(() => {
                setLoading(false);
            });
    }

    function getUtmFromLandingPage(handlLandingPage: string, key: string): string {
        if (!handlLandingPage) return "";

        try {
            const url = new URL(handlLandingPage);
            const value = url.searchParams.get(key);
            if (!value) return "";
            if (value === "unknown" || value === "(not set)") return "";
            return value;
        } catch {
            return "";
        }
    }

    function normalizeParam(v: string | null): string {
        if (!v) return "";
        const trimmed = v.trim();
        if (!trimmed) return "";
        if (trimmed.toLowerCase() === "unknown") return "";
        if (trimmed.toLowerCase() === "(not set)") return "";
        return trimmed;
    }

    async function createQuoteTracking(
        id: number,
        queryParams: URLSearchParams,
        trackingStageId: TrackingStageEnum,
        isDuplicate: boolean,
        creationIndex?: number,
        visitId?: string
    ) {
        const handlLandingPage = normalizeParam(queryParams.get("handl_landing_page"));

        const utmSource = normalizeParam(queryParams.get("utm_source")) || getUtmFromLandingPage(handlLandingPage, "utm_source") || "";
        const utmMedium = normalizeParam(queryParams.get("utm_medium")) || getUtmFromLandingPage(handlLandingPage, "utm_medium") || "";
        const utmCampaign = normalizeParam(queryParams.get("utm_campaign")) || getUtmFromLandingPage(handlLandingPage, "utm_campaign") || "";
        const utmTerm = normalizeParam(queryParams.get("utm_term")) || getUtmFromLandingPage(handlLandingPage, "utm_term") || "";

        const gclid = normalizeParam(queryParams.get("gclid"));
        const fbclid = normalizeParam(queryParams.get("fbclid"));
        const fbc = normalizeParam(queryParams.get("_fbc"));
        const fbp = normalizeParam(queryParams.get("_fbp"));

        const hasMetaClick = !!fbclid;
        const hasGoogleClick = !!gclid;

        const handlOriginalRef = normalizeParam(queryParams.get("handl_original_ref"));
        const handlRefDomain = normalizeParam(queryParams.get("handl_ref_domain"));

        const pysTrafficSource = normalizeParam(queryParams.get("pysTrafficSource"));
        const lastPysTrafficSource = normalizeParam(queryParams.get("last_pysTrafficSource"));
        const organicSource = normalizeParam(queryParams.get("organic_source"));
        const firstTrafficSource = normalizeParam(queryParams.get("first_traffic_source"));

        const seoEvidence = handlOriginalRef || organicSource;

        const isSearchEngineRef =
            /google\.|bing\.|yahoo\.|duckduckgo\.|baidu\.|yandex\.|search\.apple\.com|spotlight\.apple\.com/i.test(seoEvidence);

        const isSEO =
            !utmSource &&
            !utmMedium &&
            !utmCampaign &&
            !hasGoogleClick &&
            !hasMetaClick &&
            !!seoEvidence &&
            isSearchEngineRef;

        const pys = (pysTrafficSource || lastPysTrafficSource).toLowerCase();

        const inferredSource =
            utmSource ||
            (hasGoogleClick
                ? "google"
                : hasMetaClick
                    ? "facebook"
                    : isSEO
                        ? "SEO"
                        : pys.includes("facebook") || pys.includes("instagram")
                            ? "facebook"
                            : pys.includes("google")
                                ? "google"
                                : pys.includes("bing")
                                    ? "bing"
                                    : firstTrafficSource.toLowerCase() === "direct"
                                        ? "direct"
                                        : "UNKNOWN");

        const inferredMedium =
            utmMedium ||
            (hasGoogleClick
                ? "cpc"
                : hasMetaClick
                    ? "paid_social"
                    : isSEO
                        ? "organic"
                        : pys.includes("paid") || pys.includes("cpc")
                            ? "cpc"
                            : pys.includes("social") || pys.includes("facebook") || pys.includes("instagram")
                                ? "paid_social"
                                : firstTrafficSource.toLowerCase() === "direct"
                                    ? "direct"
                                    : "UNKNOWN");

        const inferredCampaign =
            utmCampaign ||
            (hasGoogleClick
                ? "google_ads"
                : hasMetaClick
                    ? "meta"
                    : isSEO
                        ? "SEO"
                        : (pys ? pys : "UNKNOWN"));

        const payload: IQuoteTrackingDetail = {
            ...trackingData,
            formId: id,
            brandId: params.brand,
            trackingStageId,
            source: inferredSource,
            originalSource: normalizeParam(queryParams.get("first_utm_source")) || inferredSource,

            medium: inferredMedium,
            originalMedium: normalizeParam(queryParams.get("first_utm_medium")) || inferredMedium,

            campaign: inferredCampaign,
            originalCampaign: normalizeParam(queryParams.get("first_utm_campaign")) || (utmCampaign ? utmCampaign : ""),

            keyword: utmTerm || normalizeParam(queryParams.get("first_utm_term")) || "",
            originalKeyword: normalizeParam(queryParams.get("first_utm_term")) || (utmTerm ? utmTerm : ""),

            ip: normalizeParam(queryParams.get("handl_ip")),
            gClId: gclid,

            handlLandingPage,
            handlLandingPageBase: normalizeParam(queryParams.get("handl_landing_page_base")),
            handlRef: normalizeParam(queryParams.get("handl_ref")),
            handlOriginalRef,
            handlRefDomain,
            handlUrl: normalizeParam(queryParams.get("handl_url")),
            handlUrlBase: normalizeParam(queryParams.get("handl_url_base")),
            handlID: normalizeParam(queryParams.get("handlID")),

            trafficSource:
                normalizeParam(queryParams.get("traffic_source")) ||
                pysTrafficSource ||
                (isSEO ? "SEO" : hasMetaClick ? "meta" : hasGoogleClick ? "google_ads" : ""),

            originalReferringUrl: "",

            metaFbc: fbc,
            metaFbp: fbp,
            metaFbclid: fbclid,

            isFormDuplicate: isDuplicate,
            formSequenceNo: creationIndex ?? null,
            visitId: visitId ?? null
        };

        setTrackingData(payload);

        try {
            await quoteTracking.post(payload);
        } catch (err) {
            console.warn("[QuoteTracking] Failed to save tracking", err);
        }
    }

    return (
        <div aria-busy={isLoading || undefined}>
            <LoaderDoubleLines />
        </div>
    );
}

export default Forms;